import { useEffect, useRef } from "react";

// Place fish-tree.jpeg in your project's /public/images/ folder
const FISH_TREE_IMG = "/images/fish-tree.jpeg";

const sections = [
  {
    id: "conversation",
    heading: "Understanding the Conversation Around ABA",
    body: [
      `Many families describe ABA as a life-changing intervention that helped their children communicate, develop independence, build daily living skills, and participate more fully in their communities. At the same time, some autistic adults have shared experiences that felt invalidating, exhausting, or focused more on conformity than genuine support.`,
      `These concerns deserve to be heard.`,
      `In many cases, the criticism is not that communication should be taught, safety skills should be developed, or independence should be encouraged. Rather, concerns often arise when intervention focuses on making autistic individuals appear more typical — goals such as maintaining eye contact, suppressing repetitive movements, remaining seated for long periods, or complying with adult demands without question.`,
      `The intention behind these goals was often positive. Professionals and families wanted individuals to succeed in a society that values certain social norms. However, good intentions do not prevent us from reflecting on whether those goals truly served the individual. As autistic voices became increasingly prominent in these discussions, the field was challenged to consider an important possibility: perhaps some differences were never problems to begin with.`,
    ],
  },
  {
    id: "neurodiversity",
    heading: "What Neurodiversity Has Taught Us",
    body: [
      `The neurodiversity movement encourages us to view neurological differences as natural variations of human diversity rather than deficits that need to be eliminated.`,
      `Autistic individuals may communicate differently, process information differently, experience sensory input differently, and engage with the world differently. These differences are not mistakes in need of correction. They are part of the unique ways individuals experience and interact with the world.`,
      `This does not mean support is unnecessary. A child who struggles to communicate their needs may benefit greatly from learning alternative forms of communication. An individual who has difficulty understanding safety rules may need explicit teaching and support. Someone who wants friendships but struggles with social navigation may benefit from learning skills that help them build meaningful relationships. The neurodiversity perspective does not reject support — rather, it asks us to carefully consider the purpose of that support.`,
    ],
  },
  {
    id: "behaviour",
    heading: "Looking Beyond the Behaviour",
    body: [
      `One of the most valuable lessons to emerge from neurodiversity-informed practice is the recognition that behaviour does not exist in isolation.`,
      `A child who avoids eye contact may still be listening attentively and engaging fully in a conversation. A person who rocks, hums, or flaps their hands may be regulating sensory input, expressing excitement, or managing emotions. An individual who requests a break rather than immediately following a demand may be communicating a need, not demonstrating defiance.`,
      `Today, many practitioners are asking a different question. Instead of asking, "How do we stop this behaviour?" we ask, "What purpose does this behaviour serve?"`,
    ],
  },
  {
    id: "lens",
    heading: "ABA Through a Different Lens",
    body: [
      `At its core, ABA is the science of learning and behaviour. The principles themselves are neither harmful nor beneficial in isolation. Their impact depends on how they are applied and, perhaps more importantly, what goals they are used to pursue.`,
      `What if ABA stopped asking how to make an individual appear more typical and started asking how to improve that individual's quality of life?`,
      `Instead of teaching eye contact because it is socially expected, the focus becomes meaningful communication. Instead of reducing every repetitive movement, we first seek to understand whether that behaviour serves an important sensory or emotional function. Instead of prioritizing compliance above all else, we focus on developing communication, self-advocacy, flexibility, problem-solving, and independence.`,
      `Success is no longer measured by how closely someone resembles their peers. It is measured by whether they can communicate their needs, build meaningful relationships, participate in activities they enjoy, navigate their environment safely, and live with greater autonomy and confidence. This approach remains evidence-based. It remains data-driven. It remains grounded in the science of learning. But the destination has changed — the focus shifts from conformity to quality of life.`,
    ],
  },
];

export default function ABAReimaginedBlogPost() {
  const animRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.1 }
    );
    animRefs.current.forEach((r) => r && observer.observe(r));
    return () => observer.disconnect();
  }, []);

  const ref = (el) => {
    if (el && !animRefs.current.includes(el)) animRefs.current.push(el);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400;1,600&family=Inter:wght@300;400;500;600&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        body {
          background: #F7FBF8;
          color: #252525;
          font-family: 'Inter', sans-serif;
          line-height: 1.8;
        }

        /* ─── Top colour band ─── */
        .top-band {
          background: #2A6049;
          padding: 0.85rem clamp(1.5rem, 8vw, 9rem);
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .top-band .tag {
          font-size: 0.68rem;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #B8DDC8;
        }

        .top-band .dot { color: #6BAF8B; font-size: 0.5rem; }

        .top-band .org {
          font-size: 0.75rem;
          color: rgba(255,255,255,0.6);
        }

        /* ─── Hero block ─── */
        .hero {
          background: #2A6049;
          padding: clamp(3rem, 7vw, 5.5rem) clamp(1.5rem, 8vw, 9rem) 0;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 3rem;
          align-items: flex-end;
          overflow: hidden;
        }

        .hero-text { padding-bottom: 3.5rem; }

        .hero-eyebrow {
          font-size: 0.72rem;
          font-weight: 600;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: #82C99E;
          margin-bottom: 1rem;
        }

        .hero h1 {
          font-family: 'Playfair Display', serif;
          font-size: clamp(2rem, 4vw, 3.3rem);
          font-weight: 700;
          color: #fff;
          line-height: 1.18;
          margin-bottom: 1.5rem;
        }

        .hero-byline {
          font-size: 0.88rem;
          color: rgba(255,255,255,0.55);
          font-style: italic;
        }

        .hero-byline strong {
          color: rgba(255,255,255,0.85);
          font-style: normal;
          font-weight: 600;
        }

        .hero-img-wrap {
          display: flex;
          justify-content: center;
          align-items: flex-end;
        }

        .hero-img-wrap img {
          width: 100%;
          max-width: 420px;
          display: block;
          /* Float the illustration so it breaks above the bottom edge */
          margin-bottom: -2px;
          border-radius: 6px 6px 0 0;
          background: transparent;
        }

        /* ─── Wave divider ─── */
        .wave {
          display: block;
          width: 100%;
          background: #F7FBF8;
          line-height: 0;
        }

        .wave svg { display: block; width: 100%; }

        /* ─── Article grid ─── */
        .article-layout {
          display: grid;
          grid-template-columns: 1fr min(700px, 90%) 1fr;
        }

        .article-layout > * { grid-column: 2; }
        .full-bleed { grid-column: 1 / -1 !important; }

        /* ─── Opening metaphor ─── */
        .metaphor-block {
          padding: 3.5rem 0 0.5rem;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.65s ease, transform 0.65s ease;
        }

        .metaphor-block.visible { opacity: 1; transform: none; }

        .metaphor-lead {
          font-family: 'Playfair Display', serif;
          font-size: 1.55rem;
          font-style: italic;
          color: #2A6049;
          margin-bottom: 1.5rem;
          line-height: 1.45;
        }

        .metaphor-block p {
          font-size: 1.06rem;
          color: #383838;
          margin-bottom: 1.3rem;
          line-height: 1.85;
        }

        /* ─── Big question callout ─── */
        .questions-block {
          background: #EAF4EE;
          border-radius: 6px;
          padding: 2.2rem 2.5rem;
          margin: 2rem 0;
          opacity: 0;
          transform: translateY(18px);
          transition: opacity 0.65s ease, transform 0.65s ease;
        }

        .questions-block.visible { opacity: 1; transform: none; }

        .q-line {
          font-family: 'Playfair Display', serif;
          font-size: 1.25rem;
          font-style: italic;
          color: #1E4A36;
          line-height: 1.5;
          margin-bottom: 0.9rem;
          display: flex;
          gap: 0.7rem;
          align-items: baseline;
        }

        .q-line:last-child { margin-bottom: 0; }

        .q-mark {
          color: #5BAE7E;
          font-family: 'Playfair Display', serif;
          font-size: 2rem;
          font-style: normal;
          line-height: 1;
          flex-shrink: 0;
          margin-top: -0.1rem;
        }

        /* ─── Green divider ─── */
        .green-divider {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 2rem 0;
          opacity: 0;
          transform: scaleX(0.5);
          transform-origin: left;
          transition: opacity 0.8s ease, transform 0.8s ease;
        }

        .green-divider.visible { opacity: 1; transform: scaleX(1); }

        .green-divider::before,
        .green-divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: #5BAE7E;
          opacity: 0.45;
        }

        .div-icon { color: #5BAE7E; font-size: 0.9rem; letter-spacing: 0.4em; }

        /* ─── Body sections ─── */
        .body-section {
          padding: 0.3rem 0 1.4rem;
          opacity: 0;
          transform: translateY(22px);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }

        .body-section.visible { opacity: 1; transform: none; }

        .section-heading {
          font-family: 'Playfair Display', serif;
          font-size: 1.5rem;
          font-weight: 600;
          color: #2A6049;
          margin: 0.3rem 0 1rem;
          line-height: 1.3;
        }

        .body-section p {
          font-size: 1.04rem;
          color: #383838;
          margin-bottom: 1.3rem;
          line-height: 1.85;
        }

        /* ─── Pull quote ─── */
        .pull-quote {
          border-left: 3px solid #5BAE7E;
          padding: 1.3rem 1.9rem;
          background: #EAF4EE;
          margin: 1.5rem 0;
          border-radius: 0 4px 4px 0;
          opacity: 0;
          transform: translateX(-18px);
          transition: opacity 0.7s ease, transform 0.7s ease;
        }

        .pull-quote.visible { opacity: 1; transform: none; }

        .pull-quote p {
          font-family: 'Playfair Display', serif;
          font-style: italic;
          font-size: 1.22rem;
          color: #1E4A36;
          line-height: 1.6;
        }

        /* ─── Shift table (before → after) ─── */
        .shift-table {
          width: 100%;
          border-collapse: collapse;
          margin: 2rem 0;
          opacity: 0;
          transform: translateY(18px);
          transition: opacity 0.7s ease, transform 0.7s ease;
        }

        .shift-table.visible { opacity: 1; transform: none; }

        .shift-table thead tr {
          background: #2A6049;
          color: #fff;
        }

        .shift-table thead th {
          padding: 0.85rem 1.2rem;
          font-size: 0.8rem;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          font-weight: 600;
          text-align: left;
        }

        .shift-table thead th:first-child { border-radius: 4px 0 0 0; }
        .shift-table thead th:last-child  { border-radius: 0 4px 0 0; }

        .shift-table tbody tr:nth-child(even) { background: #F0F9F4; }
        .shift-table tbody tr:nth-child(odd)  { background: #fff; }

        .shift-table tbody td {
          padding: 0.85rem 1.2rem;
          font-size: 0.95rem;
          color: #383838;
          border-bottom: 1px solid #DDF0E6;
          vertical-align: top;
        }

        .shift-table tbody td:first-child {
          color: #888;
          font-style: italic;
          border-right: 2px solid #DDF0E6;
        }

        .shift-table tbody td:last-child { color: #1E4A36; font-weight: 500; }

        .arrow-cell {
          text-align: center;
          color: #5BAE7E;
          font-size: 1.1rem;
          padding: 0.85rem 0.4rem !important;
          width: 2rem;
        }

        /* ─── Closing reflection box ─── */
        .closing-reflection {
          background: #1E4A36;
          border-radius: 6px;
          padding: 2.8rem 3rem;
          margin: 2.5rem 0;
          opacity: 0;
          transform: translateY(22px);
          transition: opacity 0.7s ease, transform 0.7s ease;
        }

        .closing-reflection.visible { opacity: 1; transform: none; }

        .closing-reflection p {
          font-family: 'Playfair Display', serif;
          font-size: 1.15rem;
          font-style: italic;
          color: rgba(255,255,255,0.88);
          line-height: 1.75;
          margin-bottom: 1.1rem;
        }

        .closing-reflection p:last-child { margin-bottom: 0; }

        .closing-reflection .highlight {
          color: #82C99E;
          font-style: normal;
          font-weight: 600;
        }

        /* ─── Author card ─── */
        .author-card {
          display: flex;
          align-items: center;
          gap: 1.4rem;
          background: #fff;
          border: 1px solid #D6EDE0;
          border-radius: 6px;
          padding: 1.5rem 2rem;
          margin: 2rem 0 3rem;
          opacity: 0;
          transform: translateY(16px);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }

        .author-card.visible { opacity: 1; transform: none; }

        .author-avatar {
          width: 52px;
          height: 52px;
          border-radius: 50%;
          background: #2A6049;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'Playfair Display', serif;
          font-size: 1.4rem;
          color: #B8DDC8;
          flex-shrink: 0;
        }

        .author-info strong {
          display: block;
          font-size: 1rem;
          color: #1E4A36;
          font-weight: 600;
        }

        .author-info span {
          font-size: 0.85rem;
          color: #888;
        }

        /* ─── Footer ─── */
        .blog-footer {
          grid-column: 1 / -1;
          background: #152E21;
          color: rgba(255,255,255,0.4);
          text-align: center;
          padding: 1.6rem;
          font-size: 0.75rem;
          letter-spacing: 0.06em;
        }

        @media (max-width: 720px) {
          .hero {
            grid-template-columns: 1fr;
            padding-bottom: 0;
          }
          .hero-img-wrap img { max-width: 280px; margin: 0 auto; }
          .shift-table { font-size: 0.88rem; }
          .shift-table thead th, .shift-table tbody td { padding: 0.65rem 0.8rem; }
          .closing-reflection { padding: 2rem 1.5rem; }
          .author-card { flex-direction: column; text-align: center; }
        }
      `}</style>

      {/* ── Top nav band ── */}
      <div className="top-band">
        <span className="tag">ABA &amp; Neurodiversity</span>
        <span className="dot">●</span>
        <span className="org">Ray of Hope Center for Autism</span>
      </div>

      {/* ── Hero ── */}
      <header className="hero">
        <div className="hero-text">
          <p className="hero-eyebrow">Perspectives · Therapy &amp; Practice</p>
          <h1>ABA, Reimagined: Growing With What We've Learned</h1>
          <p className="hero-byline">
            Written by <strong>Keziah George</strong> · Center In-Charge, RHCA Electronics City
          </p>
        </div>
        <div className="hero-img-wrap">
          <img src={FISH_TREE_IMG} alt="A colourful illustration of tropical fish arranged in the shape of a tree — fish in a tree" />
        </div>
      </header>

      {/* Wave transition */}
      <div className="wave">
        <svg viewBox="0 0 1440 60" preserveAspectRatio="none" height="60">
          <path d="M0,0 C360,60 1080,0 1440,40 L1440,60 L0,60 Z" fill="#F7FBF8" />
          <path d="M0,20 C480,70 960,0 1440,30 L1440,60 L0,60 Z" fill="#F7FBF8" opacity="0.6" />
        </svg>
      </div>

      <main className="article-layout">

        {/* Opening metaphor */}
        <div className="metaphor-block" ref={ref}>
          <p className="metaphor-lead">Imagine teaching a fish to climb a tree.</p>
          <p>
            Every day, the fish is brought to the tree. Every day, it tries and fails. New strategies are introduced. More effort is invested. More goals are added. Data is collected. Progress is monitored. Yet the fish never learns to climb. The problem, however, was never the fish.
          </p>
          <p>
            For many years, support for neurodivergent individuals often focused on helping them adapt to a world designed around neurotypical expectations. Success was frequently measured by how closely an individual could fit those expectations. But as conversations around neurodiversity have grown, many professionals, families, and autistic self-advocates have begun asking an important question:
          </p>
        </div>

        {/* Big questions */}
        <div className="questions-block" ref={ref}>
          <div className="q-line">
            <span className="q-mark">"</span>
            <span>What if the goal was never to make the fish climb the tree?</span>
          </div>
          <div className="q-line">
            <span className="q-mark">"</span>
            <span>What if the goal was to help the fish thrive in the water?</span>
          </div>
        </div>

        <div className="metaphor-block" ref={ref} style={{ paddingTop: "0.5rem" }}>
          <p>This question sits at the heart of many discussions surrounding Applied Behavior Analysis (ABA) today.</p>
        </div>

        {/* Divider */}
        <div className="green-divider" ref={ref}><span className="div-icon">✦ ✦ ✦</span></div>

        {/* Section 1 */}
        <div className="body-section" ref={ref}>
          <h2 className="section-heading">{sections[0].heading}</h2>
          {sections[0].body.map((p, i) => <p key={i}>{p}</p>)}
        </div>

        {/* Pull quote */}
        <blockquote className="pull-quote" ref={ref}>
          <p>"Perhaps some differences were never problems to begin with."</p>
        </blockquote>

        {/* Divider */}
        <div className="green-divider" ref={ref}><span className="div-icon">✦ ✦ ✦</span></div>

        {/* Section 2 */}
        <div className="body-section" ref={ref}>
          <h2 className="section-heading">{sections[1].heading}</h2>
          {sections[1].body.map((p, i) => <p key={i}>{p}</p>)}
        </div>

        {/* Pull quote */}
        <blockquote className="pull-quote" ref={ref}>
          <p>"Neurodiversity does not reject support — it asks us to carefully consider the purpose of that support."</p>
        </blockquote>

        {/* Divider */}
        <div className="green-divider" ref={ref}><span className="div-icon">✦ ✦ ✦</span></div>

        {/* Section 3 */}
        <div className="body-section" ref={ref}>
          <h2 className="section-heading">{sections[2].heading}</h2>
          {sections[2].body.map((p, i) => <p key={i}>{p}</p>)}
        </div>

        {/* Pull quote */}
        <blockquote className="pull-quote" ref={ref}>
          <p>"Instead of asking 'How do we stop this behaviour?' we ask, 'What purpose does this behaviour serve?'"</p>
        </blockquote>

        {/* Divider */}
        <div className="green-divider" ref={ref}><span className="div-icon">✦ ✦ ✦</span></div>

        {/* Section 4 — ABA through a different lens */}
        <div className="body-section" ref={ref}>
          <h2 className="section-heading">{sections[3].heading}</h2>
          <p>{sections[3].body[0]}</p>
          <p>{sections[3].body[1]}</p>
        </div>

        {/* Before → After shift table */}
        <table className="shift-table" ref={ref}>
          <thead>
            <tr>
              <th>Old Focus</th>
              <th className="arrow-cell"></th>
              <th>Reimagined Focus</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["Teaching eye contact (social conformity)", "Meaningful communication"],
              ["Reducing all repetitive movements", "Understanding the sensory / emotional function first"],
              ["Prioritizing compliance above all", "Self-advocacy, flexibility & independence"],
              ["Measuring success by resembling peers", "Quality of life: autonomy, safety, relationships"],
              ["Fitting neurotypical expectations", "Thriving as one's authentic self"],
            ].map(([before, after], i) => (
              <tr key={i}>
                <td>{before}</td>
                <td className="arrow-cell">→</td>
                <td>{after}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="body-section" ref={ref}>
          <p>{sections[3].body[2]}</p>
          <p>{sections[3].body[3]}</p>
          <p>
            The future of ABA does not lie in defending every practice from the past, nor does it lie in abandoning a science that has helped countless individuals learn and grow. Rather, it lies in our willingness to listen, reflect, and continue learning — listening to autistic individuals, to families, to evolving research, and to the experiences of those receiving support.
          </p>
          <p style={{ fontWeight: 500, color: "#2A6049" }}>
            Growth is not a sign that a field has failed. Growth is a sign that a field is willing to learn.
          </p>
        </div>

        {/* Divider */}
        <div className="green-divider" ref={ref}><span className="div-icon">✦ ✦ ✦</span></div>

        {/* Closing reflection */}
        <div className="closing-reflection" ref={ref}>
          <p>
            At Ray of Hope, we are committed to learning alongside the field — striving to provide support that is both <span className="highlight">evidence-based</span> and respectful of the diverse ways individuals experience and engage with the world. While our understanding continues to evolve, our goal remains the same: to help individuals build meaningful skills, increase independence, and improve quality of life while honouring their individuality and dignity.
          </p>
          <p>
            Perhaps the answer was never to teach the fish to climb the tree. Perhaps the answer was to recognise that <span className="highlight">the fish was meant to swim.</span>
          </p>
          <p>
            As our understanding of neurodiversity continues to grow, so too must our approach to support. By combining evidence-based practice with respect for individuality, we can create environments where people are not expected to become someone else — but are <span className="highlight">empowered to become the best version of themselves.</span>
          </p>
        </div>

        {/* Author attribution */}
        <p style={{ textAlign: "right", fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontSize: "1.05rem", color: "#2A6049", padding: "0.5rem 0 3rem" }}>
          — Keziah George
        </p>

        <footer className="blog-footer">
          © 2026 Ray of Hope Center for Autism · Asian Christian Academy · All rights reserved
        </footer>

      </main>
    </>
  );
}
